import { useCallback, useEffect, useRef, useState } from 'react';
import {
  createPersonService,
  deletePersonService,
  getPersonServices,
  listServices,
  updatePersonService,
} from '../../services/person-services.service';

const toEndDateInput = (row) => row.endDate || '';
const toNullableString = (value) => {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
};

const toUpdateBody = (formState) => ({
  active: formState.active,
  end_date: formState.endDate === '' ? null : formState.endDate,
  ext_username: toNullableString(formState.extUsername),
  external_id: toNullableString(formState.externalId),
});

const toEditForm = (row) => ({
  active: Boolean(row.active),
  endDate: toEndDateInput(row),
  extUsername: row.extUsername || '',
  externalId: row.externalId || '',
});

const emptyAddForm = () => ({
  active: true,
  endDate: '',
  extUsername: '',
  externalId: '',
  serviceKey: '',
  startDate: '',
});

const toCreateBody = (formState) => {
  const body = {
    active: formState.active,
    service_key: Number(formState.serviceKey),
  };
  if (formState.startDate !== '') body.start_date = formState.startDate;
  if (formState.endDate !== '') body.end_date = formState.endDate;
  const extUsername = toNullableString(formState.extUsername);
  if (extUsername !== null) body.ext_username = extUsername;
  const externalId = toNullableString(formState.externalId);
  if (externalId !== null) body.external_id = externalId;
  return body;
};

/**
 * @typedef {Object} ServiceCatalogRow
 * @property {number} serviceId
 * @property {string} serviceName
 * @property {string|null} displayName
 */

/**
 * @typedef {Object} PersonServiceRow
 * @property {number} id
 * @property {string} serviceName
 * @property {string|null} displayName
 * @property {boolean|null} active
 * @property {string|null} extUsername
 * @property {string|null} externalId
 * @property {string|null} endDate
 */

export const useServiceModalHook = ({ onSaved, open, person }) => {
  const personId = person?.personId;
  const reloadGenerationRef = useRef(0);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState(/** @type {PersonServiceRow[]} */ ([]));
  const [serviceCatalog, setServiceCatalog] = useState(/** @type {ServiceCatalogRow[]} */ ([]));
  const [error, setError] = useState(/** @type {string | null} */ (null));
  const [editingId, setEditingId] = useState(/** @type {number | null} */ (null));
  const [editForm, setEditForm] = useState(() => toEditForm({}));
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(/** @type {number | null} */ (null));
  const [adding, setAdding] = useState(false);
  const [addForm, setAddForm] = useState(emptyAddForm);

  const reload = useCallback(async () => {
    if (!personId) {
      return;
    }

    const generation = reloadGenerationRef.current + 1;
    reloadGenerationRef.current = generation;

    setLoading(true);
    setError(null);
    try {
      const [personRows, catalog] = await Promise.all([getPersonServices(personId), listServices()]);
      if (reloadGenerationRef.current !== generation) {
        return;
      }

      setRows(Array.isArray(personRows) ? personRows : []);
      setServiceCatalog(Array.isArray(catalog) ? catalog : []);
    } catch (requestError) {
      if (reloadGenerationRef.current !== generation) {
        return;
      }

      console.error('PersonsManager: failed to load person services', requestError);
      setError('Failed to load person services');
      setRows([]);
      setServiceCatalog([]);
    } finally {
      if (reloadGenerationRef.current === generation) {
        setLoading(false);
      }
    }
  }, [personId]);

  useEffect(() => {
    if (open && personId) {
      setEditingId(null);
      setEditForm(toEditForm({}));
      setDeleteId(null);
      setAdding(false);
      setAddForm(emptyAddForm());
      reload();
    }
  }, [open, personId, reload]);

  const startEditRow = useCallback(
    (row) => {
      setEditingId(row.id);
      setEditForm(toEditForm(row));
    },
    [setEditingId, setEditForm],
  );

  const cancelEdit = useCallback(() => {
    setEditingId(null);
    setEditForm(toEditForm({}));
  }, []);

  const patchEditForm = useCallback((patch) => {
    setEditForm((prev) => ({ ...prev, ...patch }));
  }, []);

  const patchAddForm = useCallback((patch) => {
    setAddForm((prev) => ({ ...prev, ...patch }));
  }, []);

  const cancelAdd = useCallback(() => {
    setAdding(false);
    setAddForm(emptyAddForm());
  }, []);

  const saveEdit = useCallback(
    async (row) => {
      if (!personId) {
        return;
      }

      setSaving(true);
      setError(null);
      try {
        await updatePersonService(personId, row.id, toUpdateBody(editForm));
        cancelEdit();
        await reload();
        if (onSaved) {
          onSaved();
        }
      } catch (requestError) {
        console.error('PersonsManager: failed to update person service', requestError);
        setError('Update failed');
      } finally {
        setSaving(false);
      }
    },
    [cancelEdit, editForm, onSaved, personId, reload],
  );

  const handleDelete = useCallback(async () => {
    if (!personId || deleteId === null || deleteId === undefined) {
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await deletePersonService(personId, deleteId);
      setDeleteId(null);
      await reload();
      if (onSaved) {
        onSaved();
      }
    } catch (requestError) {
      console.error('PersonsManager: failed to delete person service', requestError);
      setError('Delete failed');
    } finally {
      setSaving(false);
    }
  }, [deleteId, onSaved, personId, reload]);

  const handleAdd = useCallback(async () => {
    if (!personId || addForm.serviceKey === '') {
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await createPersonService(personId, toCreateBody(addForm));
      cancelAdd();
      await reload();
      if (onSaved) {
        onSaved();
      }
    } catch (requestError) {
      console.error('PersonsManager: failed to create person service', requestError);
      setError('Create failed');
    } finally {
      setSaving(false);
    }
  }, [addForm, cancelAdd, onSaved, personId, reload]);

  return {
    addForm,
    adding,
    cancelAdd,
    cancelEdit,
    deleteId,
    editForm,
    editingId,
    error,
    handleAdd,
    handleDelete,
    loading,
    patchAddForm,
    patchEditForm,
    rows,
    saveEdit,
    saving,
    serviceCatalog,
    setAdding,
    setDeleteId,
    startEditRow,
  };
};
